import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { SERVICES } from "../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = {
  name: "",
  phone: "",
  email: "",
  company: "",
  service: "",
  project_location: "",
  message: "",
};

export default function QuoteForm() {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.service) {
      toast.error("Please fill name, phone, and service.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") delete payload[k];
      });
      await axios.post(`${API}/quote`, payload);
      setDone(true);
      setForm(empty);
      toast.success("Quote request received. We'll be in touch shortly.");
    } catch (err) {
      console.error(err);
      toast.error("Could not submit. Please call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="quote-form-success"
        className="surface p-10 flex flex-col items-start gap-4"
      >
        <div className="overline text-[var(--ac-red)]">Request Received</div>
        <h3 className="font-display text-3xl">We've got your request.</h3>
        <p className="text-zinc-400 max-w-md">
          A dispatcher will follow up within one business hour during operating hours.
          For urgent or emergency lifts, please call us directly.
        </p>
        <button
          onClick={() => setDone(false)}
          className="btn-ghost"
          data-testid="quote-form-reset"
        >
          Submit another request
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="quote-form"
      className="surface p-6 md:p-10 space-y-6"
    >
      <div className="overline text-[var(--ac-red)]">Request a Quote</div>
      <h3 className="font-display text-3xl md:text-4xl tracking-tight">
        Tell us about the lift.
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="label-floating" htmlFor="qf-name">Full Name *</label>
          <input id="qf-name" data-testid="quote-name" className="ac-input" value={form.name} onChange={update("name")} required />
        </div>
        <div>
          <label className="label-floating" htmlFor="qf-phone">Phone *</label>
          <input id="qf-phone" type="tel" data-testid="quote-phone" className="ac-input" value={form.phone} onChange={update("phone")} required />
        </div>
        <div>
          <label className="label-floating" htmlFor="qf-email">Email</label>
          <input id="qf-email" type="email" data-testid="quote-email" className="ac-input" value={form.email} onChange={update("email")} />
        </div>
        <div>
          <label className="label-floating" htmlFor="qf-company">Company</label>
          <input id="qf-company" data-testid="quote-company" className="ac-input" value={form.company} onChange={update("company")} />
        </div>
        <div>
          <label className="label-floating" htmlFor="qf-service">Service Needed *</label>
          <select id="qf-service" data-testid="quote-service" className="ac-input" value={form.service} onChange={update("service")} required>
            <option value="">Select a service…</option>
            {SERVICES.map((s) => (
              <option key={s.slug} value={s.title}>{s.title}</option>
            ))}
            <option value="Other / Not Sure">Other / Not Sure</option>
          </select>
        </div>
        <div>
          <label className="label-floating" htmlFor="qf-location">Project Location</label>
          <input id="qf-location" data-testid="quote-location" placeholder="City, FL" className="ac-input" value={form.project_location} onChange={update("project_location")} />
        </div>
      </div>

      <div>
        <label className="label-floating" htmlFor="qf-message">Job Details</label>
        <textarea id="qf-message" data-testid="quote-message" className="ac-input" rows={4} placeholder="Date, lift weight, height, access notes…" value={form.message} onChange={update("message")} />
      </div>

      <button type="submit" disabled={submitting} data-testid="quote-submit" className="btn-primary w-full sm:w-auto justify-center">
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        {submitting ? "Sending…" : "Send Request"}
      </button>
    </form>
  );
}
